import { useState } from "react";
import { Button, Modal, Form, Input, message, Tooltip, List, Typography, Spin } from "antd";
import { FilePdfOutlined } from "@ant-design/icons";
import { pdf } from "@react-pdf/renderer";
import dayjs from "dayjs";
import ActaIndividualPDF from "../pdf/ActaIndividualPDF";
import ActaOrganizacionPDF from "../pdf/ActaOrganizacionPDF";
import { getLineasActaPO } from "../../../../services/financiero/ingresosPO/entregasPOService";

const { Text } = Typography;

export default function DescargarActaButton({ record }) {

  const [open, setOpen] = useState(false);
  const [form] = Form.useForm();
  const [generando, setGenerando] = useState(false);
  const [cargandoLineas, setCargandoLineas] = useState(false);
  const [lineas, setLineas] = useState([record]);

  const handleOpen = async () => {
    form.setFieldsValue({
      codigoConvenio: "",
      objetivo: `Entrega de bienes e insumos en el marco del proyecto ${record.codigo ? record.codigo + " - " : ""}${record.proyecto || ""}.`,
      vereda: ""
    });
    setOpen(true);
    setCargandoLineas(true);
    try {
      const data = await getLineasActaPO({
        oid_participante: record.oid_participante,
        tipo_entrega: record.tipo_entrega,
        fecha_entrega: record.fecha_entrega,
        oid_bodega: record.oid_bodega
      });
      setLineas(data.length ? data : [record]);
    } catch (error) {
      console.error(error);
      setLineas([record]);
    } finally {
      setCargandoLineas(false);
    }
  };

  const handleGenerar = async () => {
    try {
      const extras = await form.validateFields();
      setGenerando(true);

      const bienes = lineas.map((l) => ({
        nombre_corto: l.nombre_corto,
        tipo_producto: l.tipo_producto,
        nombre_producto: l.nombre_producto,
        cantidad: l.cantidad,
        unidad_medida: l.unidad_medida
      }));

      const esOrganizacion = Number(record.tipo_entrega) === 2;

      const dataComun = {
        codigoConvenio: extras.codigoConvenio || "________________",
        nombreProyecto: `${record.codigo ? record.codigo + " - " : ""}${record.proyecto || ""}`,
        tipoActa: record.tipo_acta || "Parcial",
        fechaEntrega: record.fecha_entrega ? dayjs(record.fecha_entrega).format("DD/MM/YYYY") : "",
        departamento: record.departamento,
        municipio: record.municipio,
        lugarEntrega: record.lugar_entrega,
        objetivo: extras.objetivo,
        bienes,
        usuarioEntrega: record.usuario_entrega_nombre,
        usuarioSupervisor: record.usuario_supervisor_nombre
      };

      const documento = esOrganizacion
        ? (
          <ActaOrganizacionPDF data={{
            ...dataComun,
            razonSocial: record.nombre_entrega,
            nit: record.identificacion,
            representanteLegalNombre: record.representante_legal_nombre,
            representanteLegalCedula: record.representante_legal_cedula,
            camaraComercioCodigo: record.camara_comercio_codigo,
            camaraComercioFecha: record.camara_comercio_fecha
              ? dayjs(record.camara_comercio_fecha).format("DD/MM/YYYY")
              : null,
            telefono: record.telefono_contacto_entrega
          }} />
        )
        : (
          <ActaIndividualPDF data={{
            ...dataComun,
            vereda: extras.vereda,
            nombreBeneficiario: record.nombre_entrega,
            identificacion: record.identificacion,
            telefono: record.telefono_contacto_entrega
          }} />
        );

      const blob = await pdf(documento).toBlob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Acta_Entrega_${record.nombre_entrega?.replace(/\s+/g, "_") || record.oid}_${record.fecha_entrega || ""}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      message.success("PDF generado");
      setOpen(false);
    } catch (error) {
      if (error?.errorFields) return;
      console.error(error);
      message.error("Error generando el PDF");
    } finally {
      setGenerando(false);
    }
  };

  return (
    <>
      <Tooltip title="Descargar acta en PDF">
        <Button
          size="small"
          icon={<FilePdfOutlined />}
          onClick={handleOpen}
        />
      </Tooltip>
      <Modal
        open={open}
        title="Generar acta de entrega en PDF"
        onCancel={() => setOpen(false)}
        onOk={handleGenerar}
        okText="Descargar PDF"
        cancelText="Cancelar"
        confirmLoading={generando}
        okButtonProps={{ disabled: cargandoLineas }}
      >
        <Text type="secondary">
          Se incluirán {lineas.length} línea(s) de producto entregadas a{" "}
          <b>{record.nombre_entrega}</b> el {record.fecha_entrega}.
        </Text>
        <Spin spinning={cargandoLineas}>
          <List
            size="small"
            style={{ margin: "8px 0", minHeight: 40 }}
            dataSource={lineas}
            renderItem={(l) => (
              <List.Item>{l.nombre_corto || l.nombre_producto} — {l.cantidad} {l.unidad_medida}</List.Item>
            )}
          />
        </Spin>
        <Form form={form} layout="vertical">
          <Form.Item
            name="codigoConvenio"
            label="Código de convenio (encabezado del acta)"
            rules={[{ required: true, message: "Ingrese el código de convenio" }]}
          >
            <Input placeholder="Ej: FCP 866 2023 - UTF/COL/160/COL" />
          </Form.Item>
          <Form.Item name="objetivo" label="Objetivo">
            <Input.TextArea rows={3} />
          </Form.Item>
          {Number(record.tipo_entrega) === 1 && (
            <Form.Item name="vereda" label="Vereda (opcional)">
              <Input />
            </Form.Item>
          )}
        </Form>
      </Modal>
    </>
  );
}
